/**
 * Mobile Task Card Component
 *
 * Touch-optimized task card for the mobile Kanban view.
 * Features swipe actions for quick status changes.
 *
 * @module pages/technician/tasks/MobileTaskCard
 */

import React, { useState, useRef } from 'react';
import { Badge, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TASK_STATUS, TASK_PRIORITY } from '../../../utils/constants';
import { formatDateShort, parseDateOnly } from '../../../utils/formatters';
import TaskAssigneeAvatar from '../../../components/tasks/shared/TaskAssigneeAvatar';
import KebabMenu from '../../../components/common/KebabMenu';
import './MobileTaskCard.css';

const MobileTaskCard = ({
  task,
  onStatusChange,
  showSwipeActions = true,
}) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipingLeft, setIsSwipingLeft] = useState(false);
  const [isSwipingRight, setIsSwipingRight] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef(false);

  const SWIPE_THRESHOLD = 80;
  const MAX_SWIPE = 100;

  // Priority configuration
  const getPriorityConfig = (priority) => {
    const config = {
      [TASK_PRIORITY.URGENT]: { bg: 'danger', text: 'Urgente', icon: 'bi-exclamation-triangle-fill' },
      [TASK_PRIORITY.HIGH]: { bg: 'warning', text: 'Alta', icon: 'bi-arrow-up-circle-fill' },
      [TASK_PRIORITY.MEDIUM]: { bg: 'info', text: 'Media', icon: 'bi-dash-circle-fill' },
      [TASK_PRIORITY.LOW]: { bg: 'secondary', text: 'Baja', icon: 'bi-arrow-down-circle-fill' },
    };
    return config[priority] || config[TASK_PRIORITY.MEDIUM];
  };

  // Check if due date is near
  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return null;

    // Compare calendar days (local midnight to local midnight), not raw
    // instants -- `due - now` mixed a UTC-midnight-anchored due date with
    // the current time-of-day, shifting the day threshold depending on the
    // hour and timezone (e.g. Costa Rica, UTC-6). See #266.
    const due = parseDateOnly(dueDate);
    if (!due || Number.isNaN(due.getTime())) return null;
    due.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { status: 'overdue', text: 'Vencida', className: 'task-card__due--overdue' };
    if (diffDays === 0) return { status: 'today', text: 'Hoy', className: 'task-card__due--today' };
    if (diffDays <= 2) return { status: 'soon', text: `${diffDays}d`, className: 'task-card__due--soon' };
    return { status: 'normal', text: formatDateShort(dueDate), className: '' };
  };

  // Get available status transitions
  const getNextStatus = (currentStatus, direction) => {
    if (direction === 'right') {
      // Swipe right: move forward in workflow
      if (currentStatus === TASK_STATUS.PENDING) return TASK_STATUS.IN_PROGRESS;
      if (currentStatus === TASK_STATUS.IN_PROGRESS) return TASK_STATUS.COMPLETED;
    } else if (direction === 'left') {
      // Swipe left: move backward in workflow
      if (currentStatus === TASK_STATUS.IN_PROGRESS) return TASK_STATUS.PENDING;
      if (currentStatus === TASK_STATUS.COMPLETED) return TASK_STATUS.IN_PROGRESS;
    }
    return null;
  };

  const getSwipeActionLabel = (direction) => {
    const nextStatus = getNextStatus(task.status, direction);
    if (!nextStatus) return null;

    const labels = {
      [TASK_STATUS.PENDING]: 'Pendiente',
      [TASK_STATUS.IN_PROGRESS]: 'En Progreso',
      [TASK_STATUS.COMPLETED]: 'Completar',
    };
    return labels[nextStatus];
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    if (!showSwipeActions) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = false;
  };

  const handleTouchMove = (e) => {
    if (!showSwipeActions) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX.current;
    const diffY = currentY - touchStartY.current;

    // Determine if this is a horizontal or vertical swipe
    if (!isHorizontalSwipe.current && Math.abs(diffX) > 10) {
      isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
    }

    if (!isHorizontalSwipe.current) return;

    e.preventDefault();

    // Clamp the offset
    const clampedOffset = Math.max(-MAX_SWIPE, Math.min(MAX_SWIPE, diffX));
    setSwipeOffset(clampedOffset);

    // Update swipe direction indicators
    setIsSwipingRight(clampedOffset > SWIPE_THRESHOLD / 2);
    setIsSwipingLeft(clampedOffset < -SWIPE_THRESHOLD / 2);
  };

  const handleTouchEnd = () => {
    if (!showSwipeActions) return;

    if (Math.abs(swipeOffset) >= SWIPE_THRESHOLD) {
      const direction = swipeOffset > 0 ? 'right' : 'left';
      const nextStatus = getNextStatus(task.status, direction);

      if (nextStatus && onStatusChange) {
        onStatusChange(task.id, nextStatus);
      }
    }

    // Reset swipe state
    setSwipeOffset(0);
    setIsSwipingRight(false);
    setIsSwipingLeft(false);
    isHorizontalSwipe.current = false;
  };

  const handleCardClick = () => {
    // Only navigate if not swiping
    if (Math.abs(swipeOffset) < 10) {
      navigate(`/tecnico/tareas/${task.id}`);
    }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const priorityConfig = getPriorityConfig(task.priority);
  const dueDateStatus = getDueDateStatus(task.due_date);
  const leftAction = getSwipeActionLabel('left');
  const rightAction = getSwipeActionLabel('right');

  return (
    <div className="task-card-wrapper">
      {/* Swipe action backgrounds */}
      {showSwipeActions && leftAction && (
        <div className={`task-card__action task-card__action--left ${isSwipingLeft ? 'task-card__action--active' : ''}`}>
          <i className="bi bi-arrow-left-circle"></i>
          <span>{leftAction}</span>
        </div>
      )}
      {showSwipeActions && rightAction && (
        <div className={`task-card__action task-card__action--right ${isSwipingRight ? 'task-card__action--active' : ''}`}>
          <span>{rightAction}</span>
          <i className="bi bi-arrow-right-circle"></i>
        </div>
      )}

      {/* Card content */}
      <div
        ref={cardRef}
        className={`task-card ${isSwipingLeft || isSwipingRight ? 'task-card--swiping' : ''}`}
        style={{ transform: `translateX(${swipeOffset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
      >
        {/* Priority indicator strip */}
        <div className={`task-card__priority-strip task-card__priority-strip--${task.priority}`}></div>

        <div className="task-card__content">
          {/* Header: Task number, priority, and kebab menu */}
          <div className="task-card__header">
            <span className="task-card__number">
              {task.task_number || `#${task.id.slice(0, 8)}`}
            </span>
            <div className="d-flex align-items-center gap-1">
              <Badge bg={priorityConfig.bg} className="task-card__priority-badge">
                <i className={`bi ${priorityConfig.icon} me-1`}></i>
                {priorityConfig.text}
              </Badge>
              <KebabMenu
                label={`Acciones de la tarea ${task.title}`}
                toggleClassName="task-card__kebab"
                dropdownProps={{
                  show: menuOpen,
                  onToggle: (nextShow) => setMenuOpen(nextShow),
                }}
                menuProps={{ style: { zIndex: 1060 } }}
              >
                <Dropdown.Item onClick={(e) => { e.stopPropagation(); setMenuOpen(false); navigate(`/tecnico/tareas/${task.id}`); }}>
                  <i className="bi bi-eye me-2"></i>Ver Detalle
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => { e.stopPropagation(); setMenuOpen(false); navigate(`/tecnico/tareas/${task.id}/editar`); }}>
                  <i className="bi bi-pencil me-2"></i>Editar
                </Dropdown.Item>
              </KebabMenu>
            </div>
          </div>

          {/* Title */}
          <h4 className="task-card__title">{task.title}</h4>

          {/* Description (truncated) */}
          {task.description && (
            <p className="task-card__description">{task.description}</p>
          )}

          {/* Assignee avatars */}
          {task.assignees && task.assignees.length > 0 && (
            <div className="task-card__assignees">
              {task.assignees.slice(0, 3).map((assignment, idx) => {
                const user = assignment?.assigned_user || assignment?.user;
                if (!user) return null;
                return (
                  <TaskAssigneeAvatar
                    key={assignment.id || idx}
                    assignee={user}
                    size="sm"
                  />
                );
              })}
              {task.assignees.length > 3 && (
                <div className="hex-avatar hex-avatar-sm" style={{ background: '#6c757d', fontSize: '0.55rem' }}>
                  +{task.assignees.length - 3}
                </div>
              )}
            </div>
          )}

          {/* Project info */}
          {task.project && (
            <div className="task-card__project">
              <i className="bi bi-folder me-1"></i>
              <span>{task.project.title || task.project.project_number}</span>
            </div>
          )}

          {/* Footer: Due date and indicators */}
          <div className="task-card__footer">
            {dueDateStatus && (
              <div className={`task-card__due ${dueDateStatus.className}`}>
                <i className="bi bi-calendar-event me-1"></i>
                <span>{dueDateStatus.text}</span>
              </div>
            )}

            <div className="task-card__indicators">
              {task.has_attachments && (
                <span className="task-card__indicator" title="Tiene archivos">
                  <i className="bi bi-paperclip"></i>
                </span>
              )}
              {task.has_comments && (
                <span className="task-card__indicator" title="Tiene comentarios">
                  <i className="bi bi-chat-dots"></i>
                </span>
              )}
              {task.has_checklist && (
                <span className="task-card__indicator" title="Tiene checklist">
                  <i className="bi bi-list-check"></i>
                </span>
              )}
              {task.estimated_hours && (
                <span className="task-card__indicator task-card__indicator--hours" title="Horas estimadas">
                  <i className="bi bi-clock me-1"></i>
                  {task.estimated_hours}h
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Swipe progress indicator */}
        {showSwipeActions && Math.abs(swipeOffset) > 10 && (
          <div
            className={`task-card__swipe-progress ${swipeOffset > 0 ? 'task-card__swipe-progress--right' : 'task-card__swipe-progress--left'}`}
            style={{ width: `${Math.min(100, (Math.abs(swipeOffset) / SWIPE_THRESHOLD) * 100)}%` }}
          ></div>
        )}
      </div>
    </div>
  );
};

MobileTaskCard.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    task_number: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string,
    due_date: PropTypes.string,
    estimated_hours: PropTypes.number,
    has_attachments: PropTypes.bool,
    has_comments: PropTypes.bool,
    has_checklist: PropTypes.bool,
    project: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      project_number: PropTypes.string,
    }),
  }).isRequired,
  onStatusChange: PropTypes.func,
  showSwipeActions: PropTypes.bool,
};

export default MobileTaskCard;
